import type { BaseType } from 'antd/lib/typography/Base';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';

import './SpecificationField.scss';

import { SpecificationDimensionsField } from 'src/components/base/fields/SpecificationField/SpecificationDimensionsField/SpecificationDimensionsField';
import { SpecificationEnumField } from 'src/components/base/fields/SpecificationField/SpecificationEnumField';
import { SpecificationIntervalField } from 'src/components/base/fields/SpecificationField/SpecificationIntervalField/SpecificationIntervalField';
import { SpecificationIntFloatField } from 'src/components/base/fields/SpecificationField/SpecificationIntFloatField/SpecificationIntFloatField';
import { TextInputField } from 'src/components/base/fields/TextInputField';
import { YesNoMultiselectField } from 'src/components/base/fields/YesNoMultiselectField';
import { YesNoSelectField } from 'src/components/base/fields/YesNoSelectField';
import type { ICustomerLeadProductSpecificationDataFormValues } from 'src/components/customers/CustomerLeadTypes';
import type { IVendorLeadProductSpecificationDataFormValues } from 'src/components/vendors/VendorLeadTypes';
import { cNs } from 'src/helpers';
import {
  type IProductSpecificationFieldReference,
  type IProductSpecificationReference,
  ProductSpecificationFieldType,
} from 'src/types/reference/IProductSpecificationReference';
import type { IVendorLeadProductSpecificationData } from 'src/types/vendors/IVendorLeadProductSpecificationData';

function isDependentFieldHidden(
  field: IProductSpecificationFieldReference,
  specification: IProductSpecificationReference,
  specificationData: IVendorLeadProductSpecificationData
) {
  if (!field.dependsOnId) {
    return false;
  }

  const dependsOnField = specification.fields.find((searchField) => searchField.id === field.dependsOnId);

  if (dependsOnField) {
    if (
      dependsOnField.type === ProductSpecificationFieldType.BOOL &&
      field.dependsOnBoolValue != null &&
      (dependsOnField.multiselect
        ? (specificationData[field.dependsOnId] as boolean[])?.includes?.(field.dependsOnBoolValue) === false
        : specificationData[field.dependsOnId] !== field.dependsOnBoolValue)
    ) {
      return true;
    }

    if (
      dependsOnField.type === ProductSpecificationFieldType.ENUM &&
      field.dependsOnEnumValueId != null &&
      (dependsOnField.multiselect
        ? (specificationData[field.dependsOnId] as string[])?.includes?.(field.dependsOnEnumValueId) === false
        : specificationData[field.dependsOnId] !== field.dependsOnEnumValueId)
    ) {
      return true;
    }
  }

  return false;
}

type SpecificationFieldProps = {
  field: IProductSpecificationFieldReference;
  specification: IProductSpecificationReference;
  specificationData: IVendorLeadProductSpecificationData;
  className?: string;
  isDisabled?: boolean;

  hideLabel?: boolean;
  fieldClassName?: (field: IProductSpecificationFieldReference) => string | undefined;
};

export function SpecificationField({
  field,
  specification,
  specificationData,
  className,
  isDisabled,
  hideLabel,
  fieldClassName,
}: Readonly<SpecificationFieldProps>) {
  const { setFieldValue, setFieldTouched } = useFormikContext<
    IVendorLeadProductSpecificationDataFormValues | ICustomerLeadProductSpecificationDataFormValues
  >();

  const fieldName = `specData.${field.id}`;

  const commonFieldProps = useMemo(() => {
    const errorType: BaseType = 'danger';

    return {
      errorType,
      name: fieldName,
      label: hideLabel ? undefined : field.name,
      description: field.description,
      disabled: isDisabled,
    };
  }, [fieldName, hideLabel, field, isDisabled]);

  const isFieldHidden = useMemo(
    () => isDependentFieldHidden(field, specification, specificationData),
    [field, specification, specificationData]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    if (isFieldHidden) {
      // Clear value if dependent field condition becomes false
      void (async () => {
        await setFieldValue(fieldName, undefined, false);
        await setFieldTouched(fieldName, false, false);
      })();
    }
  }, [isFieldHidden]);

  if (isFieldHidden) {
    return null;
  }

  return (
    <div className={cNs('specification-field__container', className)}>
      {field.type === ProductSpecificationFieldType.BOOL ? (
        field.multiselect ? (
          <YesNoMultiselectField {...commonFieldProps} className={fieldClassName?.(field)} />
        ) : (
          <YesNoSelectField {...commonFieldProps} className={fieldClassName?.(field)} />
        )
      ) : field.type === ProductSpecificationFieldType.ENUM ? (
        <SpecificationEnumField commonFieldProps={commonFieldProps} field={field} className={fieldClassName?.(field)} />
      ) : field.type === ProductSpecificationFieldType.FLOAT || field.type === ProductSpecificationFieldType.INT ? (
        <SpecificationIntFloatField
          {...commonFieldProps}
          className={fieldClassName?.(field)}
          specificationField={field}
        />
      ) : field.type === ProductSpecificationFieldType.TEXT ? (
        <TextInputField {...commonFieldProps} className={fieldClassName?.(field)} />
      ) : field.type === ProductSpecificationFieldType.DIM2D || field.type === ProductSpecificationFieldType.DIM3D ? (
        <SpecificationDimensionsField
          commonFieldProps={commonFieldProps}
          className={fieldClassName?.(field)}
          field={field}
        />
      ) : field.type === ProductSpecificationFieldType.INTERVAL ? (
        <SpecificationIntervalField
          commonFieldProps={commonFieldProps}
          className={fieldClassName?.(field)}
          field={field}
        />
      ) : null}
    </div>
  );
}
